import { useState } from 'react';
import { api, qs } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Empty, FlashNote, Modal, ExpiryPill, PresencePill, relativeDate, formatQty, todayLocal, typedQty } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';
import FoodIcon from '../components/FoodIcon.jsx';

/** Req #3/#4/#5: browse and adjust the real-time inventory. */
export default function Inventory() {
  const [locationId, setLocationId] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const locations = useApi('/locations');
  const inventory = useApi(`/inventory${qs({ groupBy: 'location', locationId, q: query })}`);

  // Both: the chips' per-location counts come from /locations, and a count
  // that stands still while the list under it grows reads as a bug.
  const refresh = () => { inventory.reload(); locations.reload(); };

  return (
    <div className="stack">
      <FlashNote />
      <div className="row">
        <input
          type="search"
          className="grow"
          placeholder="Search inventory…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search inventory"
        />
        <button type="button" className="primary" onClick={() => setAdding(true)}>+ Add</button>
      </div>

      <div className="chips scroll-x">
        <button
          type="button"
          className={`chip ${locationId === '' ? 'active' : ''}`}
          onClick={() => setLocationId('')}
        >
          All
        </button>
        {(locations.data || []).map((location) => (
          <button
            key={location.id}
            type="button"
            className={`chip ${String(locationId) === String(location.id) ? 'active' : ''}`}
            onClick={() => setLocationId(location.id)}
          >
            {location.name}
            <span className="tiny muted" style={{ marginLeft: 5 }}>{location.item_count}</span>
          </button>
        ))}
      </div>

      {inventory.loading && !inventory.data && <Loading />}
      <ErrorNote error={inventory.error} onRetry={inventory.reload} />

      {inventory.data?.groups?.length === 0 && (
        <Empty title="Nothing here yet">
          {query ? 'No items match that search.' : 'Scan a receipt, photograph a shelf, or add an item by hand.'}
        </Empty>
      )}

      {(inventory.data?.groups || []).map((group) => (
        <section key={group.location_id}>
          <div className="section-title">
            <h2>{group.location_name}</h2>
            <span className="count">{group.items.length}</span>
          </div>
          <div className="card list">
            {group.items.map((item) => (
              <InventoryRow
                key={item.id}
                item={item}
                onEdit={() => setEditing(item)}
                onChanged={refresh}
              />
            ))}
          </div>
        </section>
      ))}

      {editing && (
        <EditInventory
          item={editing}
          locations={locations.data || []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {adding && (
        <AddInventory
          locations={locations.data || []}
          defaultLocationId={locationId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); refresh(); }}
        />
      )}
    </div>
  );
}

/**
 * M7: one row, and every ordinary thing you might want to say about it.
 *
 * Not a swipe. A swipe in a PWA fights the browser's own back gesture and hides
 * the actions from everybody who has never been told they exist -- so the strip
 * is simply on the row, and every button is one tap. Tapping the row itself
 * still opens the sheet, which is where anything needing a number lives.
 *
 * A tracked row offers the three amounts somebody actually reports (one gone,
 * all gone, still here); a presence-only row (M5) offers the only three things
 * that are true about a jar of oil. Both apply their change locally first: the
 * request takes a moment, and a button that does nothing visible for that
 * moment is a button people press twice.
 */
function InventoryRow({ item, onEdit, onChanged }) {
  const [local, setLocal] = useState(null);
  const { busy, error, run } = useAction();
  const view = { ...item, ...(local || {}) };

  const act = (optimistic, request) => run(async () => {
    setLocal(optimistic);
    try {
      await request();
    } catch (failed) {
      // Put the row back the way it was: the shelf did not change, and a screen
      // insisting otherwise is worse than the error message.
      setLocal(null);
      throw failed;
    }
    await onChanged();
    setLocal(null);
  });

  const presence = (state) => act(
    state === 'out' ? { gone: true } : { running_low: state === 'low', presence_state: 'present' },
    () => api.post(`/inventory/${item.id}/presence`, { state }),
  );
  const consume = (amount) => act(
    amount >= item.quantity ? { gone: true } : { quantity: item.quantity - amount },
    () => api.post(`/inventory/${item.id}/consume`, { quantity: amount }),
  );
  const verify = () => act(
    { presence_state: 'present', days_since_verified: 0 },
    () => api.post(`/inventory/${item.id}/verify`, {}),
  );

  // Optimistically emptied. Left in place rather than removed, so the list does
  // not jump under the finger before the reload arrives.
  if (view.gone) {
    return (
      <div className="list-item">
        <div className="grow small muted truncate">{item.item_name} — used up</div>
      </div>
    );
  }

  const tracked = item.track_quantity !== false;

  return (
    <div className="list-item" style={{ flexWrap: 'wrap', rowGap: 8 }}>
      <button
        type="button"
        className="ghost grow"
        style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, textAlign: 'left', padding: 0 }}
        onClick={onEdit}
      >
        <FoodIcon name={item.item_name} category={item.category} imageUrl={item.image_url} />
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="truncate">{item.item_name}</div>
          <div className="tiny muted truncate">
            {tracked
              ? formatQty(view.quantity, item.unit)
              : view.running_low ? 'running low' : 'in stock'}
            {view.opened_at && ` · opened ${view.days_since_opened ?? 0}d ago`}
            {item.purchased_at && ` · bought ${relativeDate(item.purchased_at)}`}
            {item.last_verified_at && ` · checked ${relativeDate(item.last_verified_at)}`}
          </div>
        </div>
      </button>
      <ExpiryPill daysLeft={item.days_left} />
      <PresencePill state={view.presence_state} daysSinceVerified={view.days_since_verified} />

      <div className="row wrap" style={{ width: '100%', gap: 8 }}>
        {tracked ? (
          <>
            <button type="button" className="small" disabled={busy} onClick={() => consume(1)}>−1</button>
            <button type="button" className="small" disabled={busy} onClick={() => consume(item.quantity)}>Used it all</button>
            <button type="button" className="small" disabled={busy} onClick={verify}>Still here</button>
          </>
        ) : (
          <>
            <button type="button" className="small" disabled={busy} onClick={() => presence('have')}>Have</button>
            <button type="button" className="small" disabled={busy} onClick={() => presence('low')}>Low</button>
            <button type="button" className="small" disabled={busy} onClick={() => presence('out')}>Out</button>
          </>
        )}
      </div>
      {error && <div className="tiny" style={{ width: '100%' }}><ErrorNote error={error} /></div>}
    </div>
  );
}

function EditInventory({ item, locations, onClose, onSaved }) {
  const [form, setForm] = useState({
    quantity: item.quantity,
    unit: item.unit,
    location_id: item.location_id,
    expires_at: item.expires_at || '',
    purchased_at: item.purchased_at || '',
    notes: item.notes || '',
  });
  const { busy, error, run } = useAction();
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const save = () => run(async () => {
    // typedQty: a blanked field means "leave it", never Number('') === 0.
    await api.patch(`/inventory/${item.id}`, { ...form, quantity: typedQty(form.quantity) });
    onSaved();
  });

  const consume = (amount) => run(async () => {
    await api.post(`/inventory/${item.id}/consume`, { quantity: amount });
    onSaved();
  });

  const verify = () => run(async () => {
    await api.post(`/inventory/${item.id}/verify`, {});
    onSaved();
  });

  const remove = (discard) => run(async () => {
    await api.delete(`/inventory/${item.id}${discard ? '?reason=discard' : ''}`);
    onSaved();
  });

  return (
    <Modal title={item.item_name} onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <div className="field-row">
          <label>
            <span className="label-text">Quantity</span>
            <input type="number" inputMode="decimal" step="any" value={form.quantity} onChange={set('quantity')} />
          </label>
          <label>
            <span className="label-text">Unit</span>
            <input value={form.unit} onChange={set('unit')} />
          </label>
        </div>

        <label>
          <span className="label-text">Location</span>
          <select value={form.location_id} onChange={set('location_id')}>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </label>

        <div className="field-row">
          <label>
            <span className="label-text">Purchased</span>
            <input type="date" value={form.purchased_at} onChange={set('purchased_at')} />
          </label>
          <label>
            <span className="label-text">Expires</span>
            <input type="date" value={form.expires_at} onChange={set('expires_at')} />
          </label>
        </div>

        <label>
          <span className="label-text">Notes</span>
          <input value={form.notes} onChange={set('notes')} placeholder="opened, half used…" />
        </label>

        <div className="small muted">
          Last checked {relativeDate(item.last_verified_at) || 'never'}
          {item.days_since_verified > 14 && ' — worth a look'}
        </div>

        <div className="row wrap">
          <button type="button" className="small" disabled={busy} onClick={() => consume(1)}>Used 1</button>
          <button type="button" className="small" disabled={busy} onClick={() => consume(item.quantity)}>Used it all</button>
          <button type="button" className="small" disabled={busy} onClick={verify}>Still here</button>
        </div>

        <hr />

        <div className="row">
          <button type="button" className="primary grow" disabled={busy} onClick={save}>
            {busy ? <><span className="spinner" /> Saving</> : 'Save'}
          </button>
          <button type="button" className="danger" disabled={busy} onClick={() => remove(true)}>Threw out</button>
        </div>
      </div>
    </Modal>
  );
}

function AddInventory({ locations, defaultLocationId, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [form, setForm] = useState({
    quantity: 1,
    unit: '',
    location_id: defaultLocationId || locations[0]?.id || '',
    purchased_at: todayLocal(),
  });
  const { busy, error, run } = useAction();
  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  const save = () => run(async () => {
    await api.post('/inventory', {
      master_item_id: item.id,
      location_id: form.location_id,
      // Blank falls to the server's own default of 1 rather than Number('')'s 0.
      quantity: typedQty(form.quantity),
      unit: form.unit || item.default_unit,
      purchased_at: form.purchased_at || null,
    });
    onSaved();
  });

  return (
    <Modal title="Add to inventory" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <label>
          <span className="label-text">Item</span>
          <ItemPicker
            value={item}
            autoFocus
            onChange={(picked) => {
              setItem(picked);
              if (picked) setForm((f) => ({ ...f, unit: f.unit || picked.default_unit || '' }));
            }}
          />
        </label>

        <div className="field-row">
          <label>
            <span className="label-text">Quantity</span>
            <input type="number" inputMode="decimal" step="any" value={form.quantity} onChange={set('quantity')} />
          </label>
          <label>
            <span className="label-text">Unit</span>
            <input value={form.unit} onChange={set('unit')} placeholder="each" />
          </label>
        </div>

        <label>
          <span className="label-text">Location</span>
          <select value={form.location_id} onChange={set('location_id')}>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </label>

        <label>
          <span className="label-text">Purchased</span>
          <input type="date" value={form.purchased_at} onChange={set('purchased_at')} />
        </label>

        <button type="button" className="primary full" disabled={busy || !item || !form.location_id} onClick={save}>
          {busy ? <><span className="spinner" /> Adding</> : 'Add'}
        </button>
      </div>
    </Modal>
  );
}
